// v0.4.84 — Reconciliation-offender diagnostic actions.
//
// Honest follow-ups to the v0.4.83 banner. Replaces the
// misleading "Amend rate table →" button (rate tables are
// repo-versioned, operators cannot edit them) with two real
// operator paths per offender row:
//
//   1. "Inspect N txn →"   — opens a slide-over listing the
//      transactions at the offending rate. Operator confirms
//      bad source data (re-ingest / supersede) or moves on.
//
//   2. "Flag rate-table drift →" — operator's verdict that this
//      IS a real new jurisdictional rate. POSTs a write-once
//      record to vat_rate_table_drift_flags. Whoever maintains
//      /backend/rates/*.json reviews the queue and patches.
//
// The slide-over uses the shadcn Sheet primitive that's already
// in /components/ui/sheet.jsx — no new dependency.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getTransactionsAtRate, flagRateTableDrift, listRateTableDriftFlags,
  getInspectReason,
} from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { OffenderInspectDrawer } from "./OffenderInspectDrawer";
import { BlockingMessageDialog } from "../BlockingMessageDialog";
import {
  countrySupportsManualOverride, manualOverrideBoxFor,
} from "../../lib/manualOverrideConfig";


const fmtPct = (rate) => {
  const n = Number(rate);
  if (!isFinite(n)) return rate;
  return `${(n * 100).toFixed(n * 100 % 1 === 0 ? 0 : 2)}%`;
};

// v0.4.96 — `toErrorString` + `errorMessage` lifted to
// `/app/frontend/src/lib/errorMessage.js` so every catch site
// in the app gets the same Pydantic-v2-422-safe normalisation.
// See that file for the historical context (v0.4.94 React-child
// crash on offender rows).

export const OffenderActions = ({
  calcId, country, offender, periodStart, periodEnd, ruleName,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [txns, setTxns] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [err, setErr] = useState(null);

  // Flag-drift state.
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [flagErr, setFlagErr] = useState(null);
  const [note, setNote] = useState("");
  // v0.5.0 — wiring ReasonPicker means the note can contain
  // un-filled `<TOKEN>` placeholders. Block submission until
  // the picker reports valid (placeholdersOk && lengthOk).
  const [noteValid, setNoteValid] = useState(true);

  // v0.6.8 — the country-specific unblock guidance on the "Reporting
  // alone doesn't unblock" chip used to be hover-only (title= on a
  // <span>, which never even shows a pointer cursor). Sweep flagged
  // this as the most severe hover-only case in the app: it's the
  // actual next-step instruction for a blocked filing. Clicking the
  // chip now shows it as a popup.
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);

  // v0.4.97 (B) — on mount, look up whether this rate/country has
  // already been flagged for this calc. The pre-v0.4.97 UX read
  // "Drift flagged. The maintainer will review." once on the
  // successful POST, then the next page-load reset to a clean Flag
  // button — so an operator who came back two days later assumed
  // the breach was un-flagged and clicked Flag again, again, and
  // again. The badge surfaced below makes the persisted flag
  // visible at every visit, so double-flagging is impossible by
  // construction.
  const [existingFlag, setExistingFlag] = useState(null);
  useEffect(() => {
    if (!calcId || !country || offender?.rate == null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await listRateTableDriftFlags(calcId);
        const match = (data?.flags || []).find(
          (f) => f.country === country
            && String(f.rate) === String(offender.rate)
            && f.period_start === periodStart
            && f.period_end === periodEnd,
        );
        if (!cancelled && match) {
          setExistingFlag(match);
          setFlagged(true);
        }
      } catch {
        // Silent — listing is read-only and not part of the
        // critical flag path. The user can still flag fresh.
      }
    })();
    return () => { cancelled = true; };
  }, [calcId, country, offender?.rate, periodStart, periodEnd]);

  const onInspect = async () => {
    setOpen(true);
    if (txns.length > 0) return;  // cached
    setLoading(true); setErr(null);
    try {
      const data = await getTransactionsAtRate(
        calcId, country, offender.rate,
      );
      setTxns(data.transactions || []);
      setAggregate(data.aggregate || null);
    } catch (e) {
      setErr(errorMessage(e));
    }
    setLoading(false);
  };

  // v0.6.10 fix — every caller MUST invoke this with no arguments or a
  // string. The report button was bound straight to it, so React's
  // synthetic event arrived here as `noteOverride`, became
  // `operator_note`, and posting it JSON.stringify'd a DOM node:
  // "Converting circular structure to JSON ... HTMLButtonElement ...
  // __reactFiber". The error rendered under every rate row as if it were
  // advice, and the report never sent.
  const onFlag = async (noteOverride) => {
    // v0.5.0f bugfix — the v0.5.0d+12 drawer redesign filled
    // `note` via setNote() then immediately awaited onFlag(),
    // which read the STALE `note` from the React closure and
    // POSTed `operator_note: null`. Result: the flag was
    // recorded but its "why" was lost (operator looked at the
    // row and saw only "✓ Drift flagged" with no reason).
    //
    // Fix: callers pass the note explicitly. Drawer's
    // onConfirmFlag passes the server-generated reason
    // directly; the row-level "Flag rate-table drift →"
    // button keeps the legacy behaviour (no note from the
    // button itself — but we now fetch a default reason
    // server-side BEFORE posting so the audit row is never
    // silent).
    setFlagBusy(true); setFlagErr(null);
    try {
      // v0.6.10 — only a real string is a note. Any caller that hands
      // us an event (or anything else) is ignored rather than posted.
      const overrideIsNote = typeof noteOverride === "string";
      let resolvedNote = (overrideIsNote ? noteOverride : note) || null;
      if (!resolvedNote) {
        // Row-level click without drawer interaction — fetch
        // the default fix_upstream reason so we never post a
        // null operator_note.
        try {
          const r = await getInspectReason(calcId, {
            country, rate: String(offender.rate),
            decision: "fix_upstream",
          });
          resolvedNote = r?.reason || null;
        } catch {
          // If reason fetch fails (e.g. all txns excluded), the
          // POST still proceeds with null — better to record the
          // flag than to block on a side-channel failure.
        }
      }
      const res = await flagRateTableDrift(calcId, {
        country,
        rate: offender.rate,
        net: offender.net,
        vat: offender.vat,
        txn_count: offender.txn_count ?? null,
        period_start: periodStart,
        period_end: periodEnd,
        rule_name: ruleName,
        operator_note: resolvedNote,
      });
      // v0.4.97 — populate the persisted-flag badge immediately
      // without waiting for a page reload.
      if (res?.flag) setExistingFlag(res.flag);
      setFlagged(true);
    } catch (e) {
      setFlagErr(errorMessage(e));
    }
    setFlagBusy(false);
  };

  const txnLabel = offender.txn_count != null
    ? (offender.txn_count === 1 ? t("ui.country_return.review_txn_single", { count: 1, defaultValue: "Review 1 transaction →" }) : t("ui.country_return.review_txn_plural", { count: offender.txn_count, defaultValue: `Review ${offender.txn_count} transactions →` }))
    : t("ui.country_return.review_txns", { defaultValue: "Review transactions →" });

  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid={`offender-actions-${offender.rate}`}
    >
      <button
        type="button"
        data-testid={`offender-inspect-${offender.rate}`}
        data-resolution-class="self-resolve"
        data-resolution-endpoint="aggregate"
        onClick={onInspect}
        className="text-[11px] px-2.5 py-1 rounded-md bg-rose-700 hover:bg-rose-600 text-white"
      >
        {txnLabel}
      </button>
      {/* v0.5.0c+7 Q2(ii) — once drift is flagged, the operator's
          real unblock paths live behind Inspect (Route to manual
          box for DE/FR, Supersede for ES/UK/IT). Surface that
          fact right next to the Inspect button so the operator
          doesn't have to scroll up to re-read the banner pointer
          for "ok I flagged it, now what".

          v0.5.0d+2 — chip LABEL was previously "↑ unblocks calc
          today" which read as "drift-flag unblocks the calc"
          (Option-A bug: chip lies about what the action does).
          Rewritten to "↑ now Inspect to unblock" so the chip is
          honestly an arrow pointing AT the Inspect button. The
          tooltip is now country-aware (Route for DE/FR,
          Supersede for ES/UK/IT). */}
      {flagged && (() => {
        const cc = (country || "").toUpperCase();
        const hasManual = countrySupportsManualOverride(cc);
        const boxId = manualOverrideBoxFor(cc);
        const tooltip = hasManual
          ? "Reporting to the maintainer is recordkeeping only — calc stays "
            + `blocked until a maintainer patches the rate table. `
            + `Review the row → Assign to ${cc} box ${boxId} to file today `
            + "(or Replace the calculation if these are bad source data, not real sales)."
          : "Reporting to the maintainer is recordkeeping only — calc stays "
            + "blocked until a maintainer patches the rate table. "
            + "Review the row → Replace the calculation to unblock filing today "
            + `(${cc || "this country"} has no manual-other-rates box on the form).`;
        return (
          <React.Fragment key={`unblock-chip-${offender.rate}`}>
            <button
              type="button"
              data-testid={`offender-inspect-unblocks-chip-${offender.rate}`}
              // Static id on purpose: identical text regardless of which
              // rate's chip this is, and the UX-audit gate (test_v0501c8)
              // only parses a literal aria-describedby="..." string, not a
              // templated one. Every instance shares the same description.
              aria-describedby="offender-inspect-unblocks-chip-desc"
              onClick={() => setShowUnblockDialog(true)}
              className="text-[10px] px-2 py-1 rounded-md bg-rose-50 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 self-center hover:bg-rose-100 dark:hover:bg-rose-900/40"
            >{/* i18n-ignore: pinned by test_v050d2 / test_v0501c7 */}
              ↑ Reporting alone doesn&apos;t unblock — review the row above
            </button>
            <span id="offender-inspect-unblocks-chip-desc" className="sr-only">
              {t("ui.country_return.offenders.unblock_chip_desc", { defaultValue: "Opens an explanatory dialog only. Does not itself resolve, flag, or unblock anything — the dialog points you to the row above to take a real action." })}
            </span>
            <BlockingMessageDialog
              open={showUnblockDialog}
              onOpenChange={setShowUnblockDialog}
              severity="warning"
              title={t("ui.country_return.offenders.unblock_dialog_title", { defaultValue: "Reporting alone doesn't unblock this filing" })}
              testid={`offender-unblock-dialog-${offender.rate}`}
            >
              <p>{tooltip}</p>
            </BlockingMessageDialog>
          </React.Fragment>
        );
      })()}
      <button
        type="button"
        data-testid={`offender-flag-drift-${offender.rate}`}
        data-resolution-class="recordkeeping"
        data-resolution-endpoint="aggregate"
        onClick={() => onFlag()}
        disabled={flagBusy || flagged}
        title={existingFlag
          ? `Already reported ${(existingFlag.flagged_at || "").slice(0, 10)} by ${existingFlag.flagged_by || "operator"} — awaiting maintainer.`
          : undefined}
        className={`text-[11px] px-2.5 py-1 rounded-md border ${
          flagged
            ? "border-emerald-400 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 cursor-default"
            : "border-rose-400 dark:border-rose-600 text-rose-700 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-900/40"
        } ${flagBusy ? "opacity-50 cursor-wait" : ""}`}
      >
        {flagged
          ? t("ui.country_return.offenders.reported_to_maintainer", { defaultValue: "✓ Reported to maintainer" })
          : (flagBusy ? t("ui.country_return.reason_preview.reporting", { defaultValue: "Reporting…" }) : t("ui.country_return.report_rate_cta", { defaultValue: "Report rate to maintainer →" }))}
      </button>
      {/* v0.4.97 (B) — visible persisted-flag badge. Reads
          straight from `vat_rate_table_drift_flags` so the
          operator can see at every visit that their action took
          effect, even though the breach can only clear once the
          maintainer patches /backend/rates/.
          v0.5.0f — exposes operator_note on the badge so the
          operator sees the "why" right next to the row. Previous
          behaviour rendered only "flagged 2026-06-21 · awaiting
          maintainer" with no reason text visible. */}
      {existingFlag && (
        <span
          data-testid={`offender-flag-badge-${offender.rate}`}
          className="text-[10px] tabular-nums px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 self-center max-w-md"
          title={
            t("ui.country_return.offenders.badge_title_head", { at: (existingFlag.flagged_at || "").slice(0, 16).replace("T", " "), by: existingFlag.flagged_by || t("ui.country_return.typed_in.operator_fallback", { defaultValue: "operator" }), defaultValue: `Reported ${(existingFlag.flagged_at || "").slice(0, 16).replace("T", " ")} by ${existingFlag.flagged_by || t("ui.country_return.typed_in.operator_fallback", { defaultValue: "operator" })}.` }) + "\n"
            + (existingFlag.operator_note
              ? t("ui.country_return.offenders.badge_title_reason", { note: existingFlag.operator_note, defaultValue: `Reason: ${existingFlag.operator_note}` }) + "\n\n"
              : "")
            + t("ui.country_return.offenders.badge_title_tail", { defaultValue: "The block will keep showing until the maintainer patches the engine's rate table and you re-run the calculation. Your report is permanent." })
          }
        >
          {t("ui.country_return.offenders.reported_on", { defaultValue: "reported" })} {(existingFlag.flagged_at || "").slice(0, 10)}
          {existingFlag.flagged_by ? t("ui.country_return.offenders.by_name", { name: existingFlag.flagged_by, defaultValue: ` by ${existingFlag.flagged_by}` }) : ""}
          {" "}{t("ui.country_return.offenders.awaiting_maintainer", { defaultValue: "· awaiting maintainer" })}
        </span>
      )}
      {existingFlag?.operator_note && (
        <div
          data-testid={`offender-flag-note-${offender.rate}`}
          className="basis-full text-[10px] text-amber-800 dark:text-amber-200/90 leading-snug pt-1 px-1"
        >
          <span className="font-semibold">{t("ui.country_return.offenders.why", { defaultValue: "why:" })} </span>
          {existingFlag.operator_note.length > 320
            ? `${existingFlag.operator_note.slice(0, 320)}…`
            : existingFlag.operator_note}
        </div>
      )}
      {flagErr && (
        <span
          data-testid={`offender-flag-error-${offender.rate}`}
          className="text-[11px] text-rose-700 dark:text-rose-300 self-center"
        >
          {flagErr}
        </span>
      )}
      {/* v0.5.0c — drawer body extracted to OffenderInspectDrawer
          so the action row stays small and the drawer can host
          the new Supersede + Re-upload resolution paths without
          busting the 500-LOC cap. */}
      <OffenderInspectDrawer
        open={open}
        onOpenChange={setOpen}
        calcId={calcId}
        country={country}
        offender={offender}
        periodStart={periodStart}
        periodEnd={periodEnd}
        txns={txns}
        aggregate={aggregate}
        loading={loading}
        err={err}
        flagged={flagged}
        note={note}
        setNote={setNote}
        noteValid={noteValid}
        setNoteValid={setNoteValid}
        flagBusy={flagBusy}
        flagErr={flagErr}
        onFlag={onFlag}
        existingFlag={existingFlag}
        navigate={navigate}
      />
    </div>
  );
};

export default OffenderActions;
